import type { MouseEvent } from 'react';
import React from 'react';
import { Block } from '@klp/design';
import { IconName, Illustration } from '@klp/design/Illustrations';
import type { Theme } from '@material-ui/core';
import { ListItem, ListItemText, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { ProsjektRoutes } from '../../config';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { FileDownload } from '@klp/filedownload';

export type HideProsjektOptionsElementType =
    | 'detaljer'
    | 'superoffice'
    | 'sammendrag'
    | 'dokumentasjon';
export type MenuPopperProjectOptionsProps = {
    prosjektId: string;
    prosessInstansId: string;
    prosessNavn: string | null;
    prosessDefinisjonKey?: string;
    selectedElement?: HideProsjektOptionsElementType;
};
const styles = (theme: Theme) => ({
    iconContainer: {
        display: 'flex',
        alignItems: 'center',
        paddingRight: theme.spacing(2),
    },
});

export const MenuPopperProjectOptions = ({
    prosjektId,
    prosessInstansId,
    prosessNavn,
    prosessDefinisjonKey,
    selectedElement,
}: MenuPopperProjectOptionsProps) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const history = useHistory();

    const handleShowProjectSchema = (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (!prosjektId || !prosessInstansId) return;
        history.push(
            ProsjektRoutes.projectSchemaSingle(prosessInstansId, prosjektId),
            {
                routeBackToSummaryPage: false,
            },
        );
    };
    const handleShowProjectSummary = (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (!prosessInstansId || !prosjektId) return;
        history.push(
            ProsjektRoutes.projectSummary(prosessInstansId, prosjektId),
        );
    };
    const handleProsessdefinisjonDokumentasjonSummary = (
        event: MouseEvent<HTMLDivElement>,
    ) => {
        event.stopPropagation();
        if (!prosessDefinisjonKey) return;

        window.open(
            ProsjektRoutes.processdefinitionDiagram(prosessDefinisjonKey),
            '_blank',
        );
    };

    const path = ProsjektRoutes.download(prosessInstansId);
    const fileName = `${prosessNavn}.pdf`;

    return (
        <>
            <ListItem
                dense={true}
                button={true}
                onClick={handleShowProjectSchema}
                selected={selectedElement === 'detaljer'}
            >
                <Block className={classes.iconContainer}>
                    <Illustration icon={IconName.CIRCLE_INFO} />
                </Block>
                <ListItemText>
                    <Typography>
                        {t('prosjekt.table.visProsjektSkjema')}
                    </Typography>
                </ListItemText>
            </ListItem>

            <ListItem
                dense={true}
                button={true}
                onClick={handleShowProjectSummary}
                selected={selectedElement === 'sammendrag'}
            >
                <Block className={classes.iconContainer}>
                    <Illustration icon={IconName.RIGHT} />
                </Block>
                <ListItemText>
                    <Typography>
                        {t('prosjekt.table.actionMenuItems.sammendrag')}
                    </Typography>
                </ListItemText>
            </ListItem>

            {prosessDefinisjonKey && (
                <ListItem
                    dense={true}
                    button={true}
                    onClick={handleProsessdefinisjonDokumentasjonSummary}
                    selected={selectedElement === 'dokumentasjon'}
                >
                    <Block className={classes.iconContainer}>
                        <Illustration icon={IconName.DOCUMENTS} />
                    </Block>
                    <ListItemText>
                        <Typography>
                            {t('prosjekt.table.actionMenuItems.dokumentasjon')}
                        </Typography>
                    </ListItemText>
                </ListItem>
            )}

            <ListItem dense={true} button={true} onClick={() => {}}>
                <Block className={classes.iconContainer}>
                    <Illustration icon={IconName.DOWNLOAD} />
                </Block>
                <ListItemText>
                    <FileDownload path={path} name={fileName} preview={false}>
                        {t('prosjekt.table.lastNedDokument')}
                    </FileDownload>
                </ListItemText>
            </ListItem>
            <ListItem dense={true} button={true} onClick={() => {}}>
                <Block className={classes.iconContainer}>
                    <Illustration icon={IconName.SEARCH} />
                </Block>
                <ListItemText>
                    <FileDownload path={path} name={fileName} preview={true}>
                        {t('prosjekt.table.forhaandsvisDokument')}
                    </FileDownload>
                </ListItemText>
            </ListItem>
        </>
    );
};
